"use client"

import React from "react"
import { cn } from "../../lib/utils"
import { useSubscription } from "../../hooks/use-subscription"
import { Button } from "../ui/button"
import { Lock, Sparkles } from "lucide-react"
import { FormWizardProvider } from "./form-wizard-context"
import { FormWizardProgress } from "./form-wizard-progress"
import { FormWizardStep } from "./form-wizard-step"
import { FormWizardNavigation } from "./form-wizard-navigation"
import { FormWizardProps } from "./types"
import { Card, CardContent } from "../ui/card"
import { Separator } from "../ui/separator"

// Internal FormWizard component with actual implementation
const FormWizardInternal = React.forwardRef<HTMLDivElement, FormWizardProps>(({
  steps,
  currentStep = 0,
  onStepChange,
  onComplete,
  orientation = 'horizontal',
  progressType = 'linear',
  allowStepSkip = false,
  allowBackNavigation = true,
  validateOnStepChange = true,
  animationType = 'slide',
  animationDuration = 0.3,
  showStepNumbers = true,
  showStepTitles = true,
  showProgressBar = true,
  stepIconPosition = 'top',
  completedStepIcon,
  activeStepIcon,
  errorStepIcon,
  className,
  progressClassName,
  navigationClassName,
  contentClassName,
  stepClassName,
  autoSave = false,
  autoSaveDelay = 2000,
  onAutoSave,
  persistData = false,
  storageKey = "form-wizard-data"
}, ref) => {
  return (
    <FormWizardProvider
      steps={steps}
      initialStep={currentStep}
      onStepChange={onStepChange}
      onComplete={onComplete}
      validateOnStepChange={validateOnStepChange}
      allowBackNavigation={allowBackNavigation}
      allowStepSkip={allowStepSkip}
      autoSave={autoSave}
      autoSaveDelay={autoSaveDelay}
      onAutoSave={onAutoSave}
      persistData={persistData}
      storageKey={storageKey}
    >
      <div ref={ref} className={cn("w-full", className)}>
        {showProgressBar && (
          <>
            <FormWizardProgress
              className={cn("mb-12", progressClassName)}
              progressType={progressType}
              orientation={orientation}
              showStepNumbers={showStepNumbers}
              showStepTitles={showStepTitles}
              stepIconPosition={stepIconPosition}
              completedStepIcon={completedStepIcon}
              activeStepIcon={activeStepIcon}
              errorStepIcon={errorStepIcon}
            />
          </>
        )}

        <Card className={cn("border-0 shadow-none", contentClassName)}>
          <CardContent className="p-0">
            <FormWizardStep
              className={stepClassName}
              animationType={animationType}
              animationDuration={animationDuration}
            />
          </CardContent>
        </Card>

        <FormWizardNavigation
          className={cn("mt-8", navigationClassName)}
        />
      </div>
    </FormWizardProvider>
  )
})

FormWizardInternal.displayName = "FormWizardInternal"

// Export with license check
export const MoonUIFormWizardPro = React.forwardRef<HTMLDivElement, FormWizardProps>(
  (props, ref) => {
    const { hasProAccess, isLoading } = useSubscription()
    
    if (!isLoading && !hasProAccess) {
      return (
        <Card className={cn("w-full", props.className)}>
          <CardContent className="py-12 text-center">
            <div className="max-w-md mx-auto space-y-4">
              <div className="rounded-full bg-purple-100 dark:bg-purple-900/30 p-3 w-fit mx-auto">
                <Lock className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Pro Feature</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Form Wizard is available exclusively to MoonUI Pro subscribers.
                </p>
                <a href="/pricing">
                  <Button size="sm">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Upgrade to Pro
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    return <FormWizardInternal {...props} ref={ref} />
  }
)

MoonUIFormWizardPro.displayName = "MoonUIFormWizardPro"

// Alias export
export const FormWizard = MoonUIFormWizardPro
export type MoonUIFormWizardProProps = FormWizardProps

// Export types and hooks
export type { FormWizardProps, WizardStep, WizardStepContentProps } from "./types"
export { useFormWizard } from "./form-wizard-context"

// Export sub-components for advanced usage
export { FormWizardProgress } from "./form-wizard-progress"
export { FormWizardStep } from "./form-wizard-step"
export { FormWizardNavigation } from "./form-wizard-navigation"